// El aviso de la campaña del 25 de octubre: una HOJA que sube una vez al abrir
// la app. Sebastián eligió esta forma ("C · Hoja al abrir") el 20-oct-2026
// entre tres maquetas (docs/pitch/preview-invitacion-25/), sabiendo que es la
// excepción a la regla de Novedades de que un aviso jamás es una ventana
// encima. La excepción es solo para esta invitación y conserva el resto de
// las reglas de la card de Novedades, de la que es una forma:
//   - aparece cuando el conjunto pendiente trae `lo_nuevo_25` (lib/queCambio.js)
//     y se reconoce por cuenta: cerrarla de cualquier modo la reconoce, así
//     que sale UNA vez;
//   - nunca a mitad de turno (debeMostrarQueCambio en QueCambioCard) ni de
//     visita en otro mundo;
//   - se cierra siempre con "Ahora no", tocando fuera o con Escape.
//
// Ajuste de enfoque de Sebastián, 20-oct: un beneficio reconocible, una
// muestra visual real de la herramienta y una acción clara, sin llenar la
// pantalla de texto. En el orden de la invitación:
//   1. El beneficio (título) y la muestra (la viñeta real, compacta).
//   2. Una línea con lo gratis y lo que suma la membresía.
//   3. La acción: conocer, o desde el 25 probar con su botón explícito.
//   4. "Condiciones", que despliega los avisos de condiciones.
//
// Abrir la hoja no inicia la prueba. La prueba solo se activa con su botón,
// que lleva a las condiciones y al registro de tarjeta.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import Button from "../foundational/Button";
import NovedadesDetalle from "../NovedadesDetalle";
import { VinetaPae, VinetaEvolucion, VinetaTens } from "../presentacion/Vinetas";
import { invitacionPara, accionAviso, resumenAviso } from "../../lib/invitacionMembresias";
import { NOVEDADES } from "../../lib/queCambio";
import { isNative, platform } from "../../lib/platform";
import PreferenciaPromociones from "../PreferenciaPromociones";
import { cuentaDe } from "../../lib/notifications";
import { trackEvent } from "../../lib/eventTracking";
import { MODULOS } from "../../lib/modulos";

const VINETA = { pae: VinetaPae, evolucion: VinetaEvolucion, tens: VinetaTens };

// Los avisos de condiciones del 25 (Términos §6). "Condiciones" los muestra
// siempre, aunque la persona ya los haya reconocido antes: son la letra
// completa a la que el aviso tiene que dar acceso. Y suma cualquier otro
// aviso PENDIENTE del conjunto (la Política §12 promete mostrarlos ante un
// cambio material), porque cerrar esta tarjeta los reconoce a todos.
const AVISOS_CONDICIONES = ["acceso_25_octubre", "aclaracion_prueba_gratuita"];
function avisosQueMostrar(pendientes = []) {
  const ids = new Set([...AVISOS_CONDICIONES, ...pendientes.filter((n) => n.tipo === "aviso").map((n) => n.id)]);
  return NOVEDADES.filter((n) => ids.has(n.id));
}
// Lo que la RPC responde cuando el perfil en memoria quedó viejo (la prueba se
// usó en otro aparato, o ya hay membresía): refrescar en vez de reintentar.


export default function AvisoLoNuevo({
  profile, pendientes = [], onEntendido, onLoNuevo, onPerfil, onTrialIniciado, onTerminos,
}) {
  const [detalle, setDetalle] = useState(false);
  const [prueba] = useState("idle"); // idle | activando | lista | error
  const inv = invitacionPara(profile, { nativo: isNative });
  const accion = accionAviso(profile, { nativo: isNative });
  // Reconocer una sola vez, aunque se cierre durante el "Prueba activa".
  const reconocido = useRef(false);
  const reconocer = () => {
    if (reconocido.current) return;
    reconocido.current = true;
    onEntendido();
  };
  const timer = useRef(null);
  const hojaRef = useRef(null);
  const Muestra = VINETA[inv.vineta];

  const medir = (id) => trackEvent("lo_nuevo_aviso", { modulo: MODULOS.LANZAMIENTO_PLUS, payload: { segmento: inv.segmento, accion: id, forma: "hoja" } });

  // Al abrirse: se mide, el inicio de atrás no se desplaza y el foco entra a
  // la hoja (lectores de pantalla y teclado). Al cerrarse se devuelve todo.
  // Si se desmonta durante el "Prueba activa", el aviso igual queda
  // reconocido (la prueba ya se activó).
  useEffect(() => {
    medir("vista");
    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const antes = document.activeElement;
    hojaRef.current?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = previo;
      if (timer.current) { clearTimeout(timer.current); reconocer(); }
      if (antes && typeof antes.focus === "function") antes.focus({ preventScroll: true });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ahoraNo = (como) => { medir(como); reconocer(); };

  // Escape cierra; Tab no se escapa de la hoja mientras está abierta.
  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); ahoraNo("escape"); return; }
    if (e.key !== "Tab" || !hojaRef.current) return;
    const focos = [...hojaRef.current.querySelectorAll("button:not([disabled]), input:not([disabled]), a[href]")];
    if (!focos.length) return;
    const primero = focos[0];
    const ultimo = focos[focos.length - 1];
    if (e.shiftKey && (document.activeElement === primero || document.activeElement === hojaRef.current)) { e.preventDefault(); ultimo.focus(); }
    else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
  };

  // Ir a la pantalla reconoce el aviso: la persona ya lo abrió, y la pantalla
  // queda a mano desde Novedades del menú.
  const ir = (id) => {
    medir(id);
    reconocer();
    if (id === "perfil") onPerfil?.();
    else onLoNuevo?.();
  };

  const probar = () => {
    if (isNative) return;
    medir("prueba");
    window.location.assign('/?planes=1');
  };

  if (typeof document === "undefined") return null;
  return createPortal(
    <>
      <div className="fixed inset-0 z-[70] bg-black/40 animate-hoja-velo" onClick={() => ahoraNo("fuera")} aria-hidden="true" />
      <div
        ref={hojaRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lo-nuevo-hoja-titulo"
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="fixed inset-x-0 bottom-0 z-[71] mx-auto flex max-h-[90vh] w-full max-w-[480px] flex-col rounded-t-[20px] bg-paper animate-hoja-sube focus:outline-none"
        style={{ boxShadow: "0 -4px 32px rgba(0,0,0,0.12)" }}
      >
        <div className="flex flex-shrink-0 justify-center pt-3 pb-1">
          <div className="h-1 w-9 rounded-full bg-edge" />
        </div>
        <div className="flex-shrink-0 px-5 pt-1">
          <p className="text-micro uppercase tracking-wide font-semibold text-accent font-sans">Lo nuevo de MiPAE</p>
          <h2 id="lo-nuevo-hoja-titulo" className="mt-1 text-h2 font-semibold text-ink font-sans leading-tight">{inv.titulo}</h2>
        </div>

        {/* Lo que se lee: la muestra (la viñeta real en su versión compacta,
            con "Ejemplo ficticio" a la vista; es un ejemplo, así que no
            recibe foco), la línea de lo gratis y, a pedido, las condiciones.
            Se desplaza dentro de la hoja; la acción queda siempre abajo. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-3">
          {Muestra && (
            <div inert>
              <Muestra compacta />
            </div>
          )}
          <p className="mt-3 text-small text-ink-subtle font-sans leading-relaxed">{resumenAviso(profile, { nativo: isNative })}</p>
          {isNative && platform === "ios" && <div className="mt-3 border-t border-edge pt-3 pb-3"><PreferenciaPromociones userId={cuentaDe(profile)} /></div>}
          {detalle && (
            <div className="mt-3 border-t border-edge pt-3 pb-1">
              <NovedadesDetalle novedades={avisosQueMostrar(pendientes)} />
              <div className="mt-1 flex flex-wrap gap-x-4">
                {!isNative && (
                  <a href="/precios" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center text-small font-semibold text-accent font-sans underline">
                    Precios y condiciones completas
                  </a>
                )}
                {onTerminos && (
                  <button
                    type="button"
                    onClick={() => { reconocer(); onTerminos(); }}
                    className="inline-flex min-h-[44px] items-center text-small font-semibold text-accent font-sans underline"
                  >
                    Términos, sección 6
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 border-t border-edge px-5 pt-3" style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 12px)" }}>
          {prueba === "lista" ? (
            <p className="flex min-h-[44px] items-center gap-2 text-body font-semibold text-ink font-sans" role="status">
              <Check size={18} strokeWidth={2.4} className="animate-check-pop text-plus" aria-hidden="true" />
              Prueba activa · {inv.prueba.dias} días
            </p>
          ) : accion.id === "prueba" ? (
            <>
              <Button variant="primary" size="md" className="w-full" onClick={probar} disabled={prueba === "activando"}>
                {prueba === "activando" ? "Activando…" : accion.texto}
              </Button>
              <p className="mt-1.5 text-small text-ink-muted font-sans text-center">Con tarjeta y cobro automático al terminar si no cancelas antes. Una vez por cuenta.</p>
              {prueba === "error" && (
                <div className="mt-2 flex flex-wrap items-center justify-center gap-2" role="alert">
                  <p className="w-full text-center text-small text-system-error font-sans">No se pudo activar la prueba.</p>
                  <Button variant="secondary" size="sm" onClick={probar}>Reintentar</Button>
                </div>
              )}
              <Button variant="ghost" size="md" className="mt-1 w-full" onClick={() => ir(accion.secundaria.id)}>
                {accion.secundaria.texto}
              </Button>
            </>
          ) : (
            <Button variant="primary" size="md" className="w-full" onClick={() => ir(accion.id)}>
              {accion.texto}
            </Button>
          )}

          <div className="mt-1 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => { if (!detalle) medir("condiciones"); setDetalle((v) => !v); }}
              aria-expanded={detalle}
              className="inline-flex min-h-[44px] items-center text-small font-semibold text-accent font-sans underline"
            >
              {detalle ? "Ocultar condiciones" : "Condiciones"}
            </button>
            <button
              type="button"
              onClick={() => ahoraNo("ahora_no")}
              className="inline-flex min-h-[44px] items-center text-small font-semibold text-ink-muted font-sans hover:text-ink"
            >
              Ahora no
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

